import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { debounce } from "lodash";
import axios from "axios";
import { MultiSelect } from "primereact/multiselect";
import MediaSection from "./MediaSection";

// const pubDates = [
//   { name: "2024", code: "24" },
//   { name: "2025", code: "25" },
// ];
function getPubDates(startYear = 2024) {
  const currentYear = new Date().getFullYear();
  const pubDates = [];

  for (let year = startYear; year <= currentYear; year++) {
    pubDates.push({
      name: year.toString(),
      code: year.toString().slice(-2),
    });
  }

  return pubDates;
}
const pubDates = getPubDates();

const MediaGallery = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [debouncedSelectedTags, setDebouncedSelectedTags] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventOptions, setEventOptions] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaOptions, setMediaOptions] = useState(null);

  const [selectedTag, setSelectedTag] = useState([]);
  const [tagOptions, setTagOptions] = useState(null);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchText(value);
      }, 500),
    []
  );

  const debouncedSetTags = useMemo(
    () =>
      debounce((tags) => {
        setDebouncedSelectedTags(tags);
      }, 1000),
    []
  );

  const {
    data: eventsData,
    isLoading: eventsLoading,
    isError: eventsError,
  } = useQuery({
    queryKey: ["events"],
    queryFn: async () => {
      const response = await axios.get("api/resources/events");

      return response.data;
    },
    staleTime: Infinity, // Never goes stale - only loads once
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Effect to handle dropdown population when events data is loaded
  useEffect(() => {
    if (eventsData) {
      const events =
        eventsData.find((item) => item.id === "events")?.names || [];
      const media_types =
        eventsData.find((item) => item.id === "media_type")?.medias || [];
      const tags = eventsData.find((item) => item.id === "tags")?.tags || [];
      const eventOptions = events.map((event) => ({
        name: event,
        value: event,
      }));
      const mediaOptions = media_types.map((media_type) => ({
        name: media_type,
        value: media_type,
      }));

      const tagOptions = tags.map((tag) => ({ name: tag, value: tag }));

      setEventOptions(eventOptions);
      setMediaOptions(mediaOptions);
      setTagOptions(tagOptions);
    }
  }, [eventsData]);

  // Update the debounced value when searchText changes
  useEffect(() => {
    debouncedSetSearch(searchQuery);

    // Cleanup function to cancel debounce on unmount
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchQuery, debouncedSetSearch]);

  // Update debounced tags
  useEffect(() => {
    debouncedSetTags(selectedTag);
    return () => debouncedSetTags.cancel();
  }, [selectedTag, debouncedSetTags]);

  const {
    data: allMediaItems,
    isLoading: mediaItemsLoading,
    isError: mediaItemsError,
  } = useQuery({
    queryKey: ["mediaItems"],
    queryFn: async () => {
      const galleries = await axios.get("api/resources/galleries");
      return galleries.data;
    },
    refetchOnMount: "always",
  });
  const filteredMedia = useMemo(() => {
    if (!allMediaItems) return [];

    let filtered = [...allMediaItems];

    if (selectedEvent) {
      filtered = filtered.filter((item) => item.eventName === selectedEvent);
    }
    if (selectedDate) {
      filtered = filtered.filter((item) => item.dateYear === selectedDate);
    }

    if (selectedMedia) {
      filtered = filtered.filter((item) => item.mediaType === selectedMedia);
    }

    if (debouncedSelectedTags && debouncedSelectedTags.length > 0) {
      filtered = filtered.filter((item) =>
        debouncedSelectedTags.some((tag) => item.tags?.includes(tag))
      );
    }

    if (debouncedSearchText) {
      const searchLower = debouncedSearchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower) ||
          item.presenter?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [
    allMediaItems,
    selectedEvent,
    selectedDate,
    selectedMedia,
    debouncedSelectedTags,
    debouncedSearchText,
  ]);

  // For the UI, you can use:
  const isLoading = eventsLoading || mediaItemsLoading;
  const isError = eventsError || mediaItemsError;

  const getTypeColor = (type) => {
    switch (type) {
      case "AI":
        return "info";
      case "Hit Discovery":
        return "success";
      case "DNA ENCODED LIBRARY (DEL)":
        return "warning";
      case "Machine Learning":
        return "Danger";
      case "Drug Discovery":
        return "contrast";
      case "CACHE":
        return "secondary";
      default:
        return "primary";
    }
  };

  return (
    <div className="w-full min-h-screen bg-white py-48 md:py-24">
      <div className="container mx-auto px-8 md:px-20  md:flex-row items-start justify-between">
        {/* Summary Section */}
        <div className="text-black md:py-8">
          <p className="text-lg md:text-2xl">
            Explore slide decks and event recordings from the AIRCHECK
            community.
          </p>
        </div>

        {/* Filters Section */}
        <div className="flex flex-col py-2 md:flex-row md:gap-4 md:items-start">
          {/* Left Side - Filter Section */}

          <div className="w-full md:w-80 md:flex-shrink-0 bg-white/90">
            <div className="bg-white py-4 rounded-lg">
              <h2 className="text-xl text-center font-semibold mb-4 text-gray-800">
                Filters
              </h2>

              {/* Search Bar */}
              <div className="my-2">
                <div className="flex items-center gap-3">
                  {/* Magnifying Glass Icon Column */}
                  <div className="flex-shrink-0">
                    <i className="pi pi-search text-[#06002e] text-2xl" />
                  </div>

                  <span className="p-float-label w-full  border border-gray-200 rounded-lg">
                    <InputText
                      id="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="custom-input text-black rounded-xl px-2 py-2 transition duration-200 w-full"
                    />
                    <label
                      htmlFor="search"
                      className="font-medium !transition-all !duration-200"
                    >
                      Search
                    </label>
                  </span>
                  {searchQuery !== debouncedSearchText && (
                    <small className="text-gray-500">Typing...</small>
                  )}
                </div>
              </div>

              {/* Date Dropdown */}
              <div className="my-2 ml-8">
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Event
                </label>

                <Dropdown
                  value={selectedEvent}
                  options={eventOptions}
                  onChange={(e) => setSelectedEvent(e.value)}
                  optionLabel="name"
                  placeholder="Select Event"
                  // className="text-white text-sm rounded-xl border-2 border-[#06002e] hover:bg-gray hover:text-black transition duration-200 w-full "
                  className="w-full p-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white shadow-sm transition-all duration-200"
                />
              </div>

              <div className="my-2 ml-8">
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Date (Year)
                </label>

                <Dropdown
                  value={selectedDate}
                  options={pubDates}
                  onChange={(e) => setSelectedDate(e.value)}
                  optionLabel="name"
                  optionValue="name"
                  placeholder="Select Year"
                  className="w-full p-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white shadow-sm transition-all duration-200"
                />
              </div>

              <div className="my-2 ml-8">
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Media Type
                </label>

                <Dropdown
                  value={selectedMedia}
                  options={mediaOptions}
                  onChange={(e) => setSelectedMedia(e.value)}
                  optionLabel="name"
                  placeholder="Select Media"
                  className="w-full p-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white shadow-sm transition-all duration-200"
                />
              </div>

              <div className="my-2 ml-8">
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Tag
                </label>

                <MultiSelect
                  value={selectedTag || []}
                  options={tagOptions || []}
                  onChange={(e) => setSelectedTag(e.value || [])}
                  optionLabel="name"
                  placeholder="Select Tags"
                  // className="text-black text-sm rounded-xl border-2 border-[#06002e] hover:bg-gray hover:text-black transition duration-200 w-full"
                  className="w-full p-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white shadow-sm transition-all duration-200"
                  display="chip"
                  panelHeaderTemplate={() => (
                    <div className="p-multiselect-header text-center">
                      <span className="font-bold text-center">Select Tags</span>
                    </div>
                  )}
                  selectedItemTemplate={(option) => {
                    if (!option) return null;

                    const displayName =
                      typeof option === "string"
                        ? option
                        : option.name || option.value;
                    return (
                      <div className="text-small flex align-items-left gap-2 px-2 py-1 rounded-lg mr-1 my-1">
                        <span className="text-sm text-gray-700">
                          {displayName}
                        </span>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTag((prev) =>
                              (prev || []).filter((tag) =>
                                typeof tag === "string"
                                  ? tag !== displayName
                                  : tag.value !== option.value
                              )
                            );
                          }}
                          className="flex items-center justify-center w-4 h-4 text-xs rounded-full text-black hover:bg-blue-500"
                        >
                          ×
                        </button>
                      </div>
                    );
                  }}
                />
              </div>

              <div className="flex-grow mt-4 text-center">
                {" "}
                <Button
                  label="Clear Filters"
                  icon="pi pi-filter-slash"
                  onClick={() => {
                    setSelectedDate("");
                    setSearchQuery("");
                    setDebouncedSearchText("");
                    setDebouncedSelectedTags([]);
                    setSelectedTag([]);
                    setSelectedMedia("");
                    setSelectedEvent("");
                  }}
                  className="text-[#06002e] font-bold rounded-xl px-4 py-2 font-bold border-2 border-[#f36548] hover:bg-[#f36548] hover:text-black transition duration-200"
                />{" "}
              </div>
              {/* Loading State */}
              {isLoading && (
                <div className="flex justify-center items-center p-8">
                  <ProgressSpinner
                    style={{ width: "50px", height: "50px" }}
                    strokeWidth="4"
                  />
                  <span className="ml-3 text-lg">Loading media items...</span>
                </div>
              )}

              {isError && (
                <div className="text-center p-8 bg-red-50 text-red-700 rounded">
                  <h3 className="text-xl mb-2">Error loading media</h3>

                  <Button
                    label="Try Again"
                    icon="pi pi-refresh"
                    className="mt-4"
                  />
                </div>
              )}
            </div>
          </div>

          {isLoading && (
            <div className="w-full py-16 md:flex-1 md:pl-4 grid grid-cols-1  gap-3">
              {isLoading && (
                <>
                  <div className="flex justify-center items-center p-8">
                    <ProgressSpinner
                      style={{ width: "250px", height: "250px" }}
                      strokeWidth="4"
                    />
                  </div>
                  <div className="flex justify-center items-center p-8">
                    <span className="ml-3 text-lg">Loading media items...</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="w-full py-16 md:flex-1 md:pl-4 grid grid-cols-1  gap-3">
              <div className="text-center p-8 bg-red-50 text-red-700 rounded">
                <h3 className="text-xl mb-2">Error loading media</h3>
                <p>{isError?.message || "An unknown error occurred"}</p>
                <Button
                  label="Try Again"
                  icon="pi pi-refresh"
                  className="mt-4"
                />
              </div>
            </div>
          )}

          {/* Gallery Grid */}
          {!isLoading && !isError && filteredMedia && (
            <MediaSection filteredMedia={filteredMedia} />
          )}

          {!isLoading &&
            !isError &&
            filteredMedia &&
            filteredMedia.length === 0 && (
              <div className="mt-32 flex items-start justify-center min-h-[400px]">
                <div className="text-center px-20 py-20 bg-gray-100 rounded">
                  <h3 className="text-xl mb-2">No media items found</h3>
                  <p>Try adjusting your filter criteria or search terms.</p>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default MediaGallery;
